import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicApi } from '../services/api';
import { companyOf, DEFAULT_COMPANY } from '../utils/company';
import PlanPicker from '../components/PlanPicker';
import OneTimeExtras from '../components/OneTimeExtras';
import { useQuery } from 'react-query';
import { MessageSquare, Phone } from 'lucide-react';
import { PublicPage, Panel, H1, H2, ButtonPrimary, ButtonGhost } from '../components/public';

/**
 * The lawn plans page anyone can open — the link the owner texts.
 *
 * Owner, Sep 17 2026: three recommended plans, and the customer can change
 * anything before sending it. A customer who opens it from their own page link
 * (?token=…) never has to type their lawn size: we already measured it.
 *
 * Rebuilt Sep 19 2026 on the shared customer skin, so a link texted from a
 * door hanger lands on something that looks like the website it came from.
 */

export default function PlansPublic() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { data } = useQuery('public-company', () => publicApi.get('/health').then(r => r.data).catch(() => null), { retry: false });
  const co = companyOf(data) || DEFAULT_COMPANY;

  return (
    <PublicPage company={co} context="Lawn treatment plans">
      <section className="bg-white rounded-3xl border border-brand-100 shadow-lift px-5 py-5">
        <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">Lawn treatments</p>
        <H1 className="mt-1">Three plans for your lawn</H1>
        <p className="text-[15px] text-gray-800 mt-2">
          Pick one, then change anything you like. Nothing is booked or charged here. What you send comes straight to
          Michael, and he confirms the price with you before anything happens.
        </p>
      </section>

      <Panel><PlanPicker api={publicApi} token={token} /></Panel>

      <OneTimeExtras api={publicApi} token={token} />

      <Panel title="How a treatment visit works">
        <p className="text-[15px] text-gray-800">
          We mow first, then treat. Hold water until the next morning. Keep pets and kids off until it dries,
          about two hours. You do not need to be home. Weather can slide a visit by a week.
        </p>
        <p className="text-[14px] text-gray-700 mt-3">
          No weed-free promise. Water, shade and soil still decide most of it. No contract, pause any time.
        </p>
      </Panel>

      <Panel tone="brand">
        <H2>Rather just ask us?</H2>
        <p className="text-[15px] text-gray-900 mt-2">Send us the address and we will price it for your yard. No pressure either way.</p>
        <div className="mt-4 space-y-2">
          <ButtonPrimary href={`sms:${co.phone_raw || DEFAULT_COMPANY.phone_raw}`}>
            <MessageSquare className="h-5 w-5" aria-hidden /> Text {co.phone || DEFAULT_COMPANY.phone}
          </ButtonPrimary>
          <ButtonGhost href={`tel:${co.phone_raw || DEFAULT_COMPANY.phone_raw}`}>
            <Phone className="h-5 w-5" aria-hidden /> Call us
          </ButtonGhost>
        </div>
      </Panel>
    </PublicPage>
  );
}
